import { createContext, useContext, useMemo, type ReactNode } from 'react';

import { daysLeft, payments, TIER_RANK, type Payment, type Plan, type Tier } from '@/api';
import { useAuth } from '@/auth';
import { dataOf, useAsync, useRefreshable } from '@/hooks';
import { SHOW_PAYMENTS } from '@/pro';

interface PlanState {
  plan: Plan;
  daysLeft: number;
  pending: Payment | null;
  deletion: string | null;
  refresh: () => Promise<void>;
  has: (tier: Tier) => boolean;
}

const FREE: Plan = { tier: 'free', until: null, previous: null };

const PlanContext = createContext<PlanState>({
  plan: FREE,
  daysLeft: 0,
  pending: null,
  deletion: null,
  refresh: () => Promise.resolve(),
  has: (tier) => tier === 'free',
});

function loadPlan(guest: boolean) {
  return Promise.all([
    payments.getPlan(),
    SHOW_PAYMENTS && !guest ? payments.getPending().catch(() => null) : null,
    guest ? null : payments.getDeletion().catch(() => null),
  ]).then(([plan, pending, deletion]) => ({ plan, pending, deletion }));
}

export function PlanProvider({ children }: { children: ReactNode }) {
  const [tick, refresh] = useRefreshable();
  const guest = useAuth().status === 'guest';
  const state = dataOf(useAsync(() => loadPlan(guest), [tick, guest], { keep: true }));

  const value = useMemo<PlanState>(() => {
    const plan = state?.plan ?? FREE;
    return {
      plan,
      daysLeft: daysLeft(plan.until),
      pending: state?.pending ?? null,
      deletion: state?.deletion ?? null,
      refresh,
      has: (tier) => TIER_RANK[plan.tier] >= TIER_RANK[tier],
    };
  }, [state, refresh]);

  return <PlanContext.Provider value={value}>{children}</PlanContext.Provider>;
}

export function usePlan(): PlanState {
  return useContext(PlanContext);
}
